import DsButton from "@components/services/DsButton";

interface ManagedTeaserBullet {
  icon?: string;
  title: string;
  description: string;
}

interface ManagedTeaserCta {
  text: string;
  href: string;
}

interface ManagedTeaserBlockProps {
  eyebrow?: string;
  title: string;
  titleHighlight?: string;
  description: string;
  bullets: ManagedTeaserBullet[];
  primaryCta: ManagedTeaserCta;
  secondaryCta?: ManagedTeaserCta;
  footnote?: string;
}

/**
 * ManagedTeaserBlock
 *
 * Day-2 / cross-sell teaser used by `managedTeaser`-style sections. Two-column
 * layout: copy + CTAs on the left, bullet list with icons on the right.
 * Reusable for any "after the project, here is the next step" upsell band.
 */
export default function ManagedTeaserBlock({
  eyebrow,
  title,
  titleHighlight,
  description,
  bullets,
  primaryCta,
  secondaryCta,
  footnote,
}: ManagedTeaserBlockProps) {
  if (!Array.isArray(bullets) || bullets.length === 0) {
    throw new Error("ManagedTeaserBlock: `bullets` array is empty or invalid.");
  }
  if (!primaryCta?.text || !primaryCta?.href) {
    throw new Error("ManagedTeaserBlock: `primaryCta.text` and `primaryCta.href` are required.");
  }
  if (secondaryCta && (!secondaryCta.text || !secondaryCta.href)) {
    throw new Error(
      "ManagedTeaserBlock: `secondaryCta.text` and `secondaryCta.href` are required when `secondaryCta` exists."
    );
  }

  return (
    <div className="managed-teaser">
      <div className="managed-teaser-grid">
        <div className="managed-teaser-copy">
          {eyebrow ? <span className="managed-teaser-eyebrow">{eyebrow}</span> : null}
          <h3 className="managed-teaser-title">
            {title}{" "}
            {titleHighlight ? (
              <span className="managed-teaser-highlight">{titleHighlight}</span>
            ) : null}
          </h3>
          <p className="managed-teaser-desc">{description}</p>
          <div className="managed-teaser-actions">
            <DsButton
              href={primaryCta.href}
              label={primaryCta.text}
              variant="primary"
            />
            {secondaryCta ? (
              <DsButton
                href={secondaryCta.href}
                label={secondaryCta.text}
                variant="ghost-inverse"
              />
            ) : null}
          </div>
          {footnote ? <p className="managed-teaser-footnote">{footnote}</p> : null}
        </div>
        <ul className="managed-teaser-bullets">
          {bullets.map((bullet) => (
            <li key={bullet.title}>
              <h5>{bullet.title}</h5>
              <p>{bullet.description}</p>
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
}
